/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import React from "react";

export interface ITableColumn<T> {
  key: string;
  title?: React.ReactNode;
  render: (record: T) => React.ReactNode;
  align?: "left" | "center" | "right";
  width?: number;
  className?: string;
}

export interface ITableProps<T> {
  columns: ITableColumn<T>[];
  data?: T[];
  rowKey: keyof T;
  className?: string;
  rowClassName?: string;
  emptyText?: React.ReactNode;
}

export const Table = <T extends object>({
  columns,
  data,
  rowKey,
  className,
  rowClassName,
  emptyText,
}: ITableProps<T>) => {
  const hasHeader = columns.some((column) => column.title);
  const hasWidths = columns.some((column) => column.width);

  return (
    <table className={className}>
      {hasWidths && (
        <colgroup>
          {columns.map((column) => (
            <col key={column.key} style={{ width: column.width }} />
          ))}
        </colgroup>
      )}
      {hasHeader && (
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                className={column.className}
                style={{ textAlign: column.align }}
              >
                {column.title}
              </th>
            ))}
          </tr>
        </thead>
      )}
      <tbody>
        {data?.length ? (
          data.map((record) => (
            <tr key={String(record[rowKey])} className={rowClassName}>
              {columns.map((column) => (
                <td
                  key={column.key}
                  className={column.className}
                  style={{ textAlign: column.align }}
                >
                  {column.render(record)}
                </td>
              ))}
            </tr>
          ))
        ) : (
          <tr className={rowClassName}>
            <td colSpan={columns.length}>{emptyText}</td>
          </tr>
        )}
      </tbody>
    </table>
  );
};

export default Table;
